import { DevTool } from "@hookform/devtools";
import { Box, Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import React, { ChangeEvent, ComponentType, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  useGetPutSpecificUserCsvDownload,
  usePutSpecificUserCsvUpload,
} from "../../api/products";
import useNotify from "../../hooks/useNotify";
import {
  AUCTION_TYPE,
  COHORT_TYPE,
  COUNTRY,
  IS_ON_DEMAND,
  LIMIT_FREQUENCY,
  PAYMENT_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_STATUS,
  PRODUCT_TYPE,
  PROMO_TAG,
  ProductDetails,
  ProductRequest,
  VOUCHER_TYPE,
} from "../../models/Product";
import { MODE, useFormStore } from "../../stores/formStore";
import { useUserStore } from "../../stores/userStore";
import { mono } from "../../theme/colors";
import DateField from "../atoms/DateField";
import DateTimeField from "../atoms/DateTimeField";
import { IMAGE_SIZE } from "../atoms/Image";
import ImageUploadField from "../atoms/ImageUploadField";
import SelectField from "../atoms/SelectField";
import Text from "../atoms/Text";
import TextField from "../atoms/TextField";
import ToggleField from "../atoms/ToggleField";
import {
  Cambodia,
  HongKong,
  Indonesia,
  Japan,
  Malaysia,
  Philippines,
  Singapore,
  Thailand,
  Vietnam,
} from "../icons/countries";
import DownloadIcon from "../icons/ui-actions/DownloadIcon";
import UploadIcon from "../icons/ui-actions/UploadIcon";
import DetailPageHeader from "../organisms/createProductHeader/DetailPageHeader";
import FormSection from "../organisms/FormSection";
import LanguageFormSection from "../organisms/LanguageFormSection";
import ProductInventory from "../organisms/ProductInventory";

type Props = {
  productDetails?: ProductDetails;
  formDefaultValues: ProductRequest;
};

type Option = {
  label: string;
  value: string;
};

type Country = Option & {
  LeftIcon?: ComponentType;
};

type ToggleOptions = Country[];

const ProductForm = ({ productDetails, formDefaultValues }: Props) => {
  const formMethods = useForm<ProductRequest>({
    defaultValues: formDefaultValues,
  });
  const { productId } = useParams<{
    productId: string;
  }>();

  const UploadableProductStatus = [
    PRODUCT_STATUS.DEACTIVATED,
    PRODUCT_STATUS.DRAFT,
    PRODUCT_STATUS.REJECTED,
  ];
  const mode = useFormStore(state => state.mode);

  const isEditMode = mode === MODE.EDIT;

  const isUploadDisabled =
    !productId ||
    (productDetails?.status &&
      !UploadableProductStatus.includes(productDetails?.status)) ||
    !isEditMode;
  const isDisabled = !productId;

  const inputRef = React.useRef<HTMLInputElement>(null);

  const isHaveAuction =
    formMethods.watch("productDetails.productCategory") ===
    PRODUCT_CATEGORY.AUCTION;

  const isNoneUserLimitFrequency = !formMethods.watch(
    "productDetails.userLimitFrequency",
  );

  const isReward =
    formMethods.watch("productDetails.productCategory") ===
    PRODUCT_CATEGORY.REWARD;

  const isSpecificUser =
    formMethods.watch("productDetails.cohortType") === COHORT_TYPE.SPECIFICUSER;

  const isNoneVoucherLimitFrequency = !formMethods.watch(
    "productDetails.voucherLimitFrequency",
  );

  const isCampaign =
    formMethods.watch("productDetails.productCategory") ===
    PRODUCT_CATEGORY.CAMPAIGN;

  const isAuctionSpecificPeriod =
    formMethods.watch("productDetails.productCategory") ===
      PRODUCT_CATEGORY.AUCTION &&
    formMethods.watch("productDetails.auctionType") === AUCTION_TYPE.SPECIFIC;

  const { setNotifySuccess, setNotifyError } = useNotify();

  const { mutateAsync: putCsvUpload, isLoading: isUploading } =
    usePutSpecificUserCsvUpload(productId);
  const { mutateAsync: getCsvDownload, isLoading: isDownloading } =
    useGetPutSpecificUserCsvDownload(productId);

  const onHandleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const csvFile = event.target.files && event.target.files[0];

      if (!csvFile) {
        throw new Error("File not found");
      }

      if (!csvFile.type.match("text/csv")) {
        throw new Error("File format must be .csv");
      }
      await putCsvUpload(csvFile);

      setNotifySuccess("The CSVS file have been auto saved.");
      await formMethods.trigger();
    } catch (error) {
      setNotifyError("Failed to upload file");
      console.error(error);
    }
  };

  const onClickUpload = async () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onClickDownload = async () => {
    try {
      await getCsvDownload();
    } catch (error) {
      setNotifyError("No downloadable file available");
    }
  };

  useEffect(() => {
    if (isNoneUserLimitFrequency) {
      formMethods.setValue("productDetails.userLimitNumber", "");
      formMethods.clearErrors("productDetails.userLimitNumber");
    }
    if (isNoneVoucherLimitFrequency) {
      formMethods.setValue("productDetails.voucherLimitNumber", "");
      formMethods.clearErrors("productDetails.voucherLimitNumber");
    }
  }, [isNoneUserLimitFrequency, isNoneVoucherLimitFrequency]);

  useEffect(() => {
    if (isHaveAuction) {
      formMethods.setValue("productDetails.userLimitFrequency", "");
      formMethods.setValue("productDetails.userLimitNumber", "");
      formMethods.setValue("productDetails.voucherLimitFrequency", "");
      formMethods.setValue("productDetails.voucherLimitNumber", "");
      formMethods.setValue("productDetails.paymentType", PAYMENT_TYPE.POINTS);
    } else {
      formMethods.setValue("productDetails.auctionType", "");
    }
  }, [isHaveAuction]);

  useEffect(() => {
    if (isCampaign) {
      formMethods.setValue("productDetails.cohortType", COHORT_TYPE.ALL);
    }
  }, [isCampaign]);

  const permissionCountries = useUserStore(state => state.country);
  let countriesOption: ToggleOptions = [];

  if (
    !permissionCountries ||
    (permissionCountries?.length === 1 && permissionCountries[0] === "ALL")
  ) {
    countriesOption = [
      {
        value: COUNTRY.MALAYSIA,
        label: "Malaysia",
        LeftIcon: Malaysia,
      },
      {
        value: COUNTRY.INDONESIA,
        label: "Indonesia",
        LeftIcon: Indonesia,
      },
      {
        value: COUNTRY.CAMBODIA,
        label: "Cambodia",
        LeftIcon: Cambodia,
      },
      { value: COUNTRY.JAPAN, label: "Japan", LeftIcon: Japan },
      {
        value: COUNTRY.HONG_KONG,
        label: "Hong Kong",
        LeftIcon: HongKong,
      },
      {
        value: COUNTRY.PHILIPPINES,
        label: "Philippines",
        LeftIcon: Philippines,
      },
      {
        value: COUNTRY.SINGAPORE,
        label: "Singapore",
        LeftIcon: Singapore,
      },
      {
        value: COUNTRY.VIETNAM,
        label: "Vietnam",
        LeftIcon: Vietnam,
      },
      {
        value: COUNTRY.THAILAND,
        label: "Thailand",
        LeftIcon: Thailand,
      },
    ];
  } else {
    permissionCountries.map(item => {
      switch (item) {
        case COUNTRY.MALAYSIA:
          countriesOption.push({
            value: COUNTRY.MALAYSIA,
            label: "Malaysia",
            LeftIcon: Malaysia,
          });
          break;
        case COUNTRY.INDONESIA:
          countriesOption.push({
            value: COUNTRY.INDONESIA,
            label: "Indonesia",
            LeftIcon: Indonesia,
          });
          break;
        case COUNTRY.CAMBODIA:
          countriesOption.push({
            value: COUNTRY.CAMBODIA,
            label: "Cambodia",
            LeftIcon: Cambodia,
          });
          break;
        case COUNTRY.JAPAN:
          countriesOption.push({
            value: COUNTRY.JAPAN,
            label: "Japan",
            LeftIcon: Japan,
          });
          break;
        case COUNTRY.HONG_KONG:
          countriesOption.push({
            value: COUNTRY.HONG_KONG,
            label: "Hong Kong",
            LeftIcon: HongKong,
          });
          break;
        case COUNTRY.PHILIPPINES:
          countriesOption.push({
            value: COUNTRY.PHILIPPINES,
            label: "Philippines",
            LeftIcon: Philippines,
          });
          break;
        case COUNTRY.SINGAPORE:
          countriesOption.push({
            value: COUNTRY.SINGAPORE,
            label: "Singapore",
            LeftIcon: Singapore,
          });
          break;
        case COUNTRY.VIETNAM:
          countriesOption.push({
            value: COUNTRY.VIETNAM,
            label: "Vietnam",
            LeftIcon: Vietnam,
          });
          break;
        case COUNTRY.THAILAND:
          countriesOption.push({
            value: COUNTRY.THAILAND,
            label: "Thailand",
            LeftIcon: Thailand,
          });
          break;
        default:
          break;
      }
    });
  }

  return (
    <Box data-testid="inventory_create-product-form">
      <FormProvider {...formMethods}>
        <DetailPageHeader status={productDetails?.status} />
        {productDetails?.status === PRODUCT_STATUS.REJECTED && (
          <Stack
            width={"100%"}
            spacing={1}
            bgcolor="primary.light"
            alignItems={"center"}
            paddingY={2}
          >
            <Stack
              direction={"column"}
              width={"1227px"}
              data-testid="product-form_reject-reason_container"
            >
              <Text variant="title_xsmall">Rejected reason</Text>
              <Text variant="body_small">{productDetails?.statusReason}</Text>
            </Stack>
          </Stack>
        )}
        <Stack
          direction={"row"}
          spacing={2}
          px={10}
          justifyContent={"center"}
          mt={2}
        >
          <form>
            <Stack direction="row" spacing={3}>
              <Stack direction={"column"} spacing={2} width={"824px"}>
                <FormSection sectionTitle="Product Details" direction="row">
                  <>
                    <Stack spacing={2} width={"-webkit-fill-available"}>
                      <TextField
                        name="productDetails.brandName"
                        label="Brand Name"
                      />
                      <TextField
                        name="productDetails.productId"
                        label="OMNE Product ID"
                        disabled={true}
                      />
                      <TextField
                        name="productDetails.externalProductId"
                        label="External Product ID"
                      />
                      <TextField
                        name="productDetails.productName"
                        label="Product Name"
                      />
                      <TextField
                        name="productDetails.merchantUrl"
                        label="Merchant URL"
                      />
                      <TextField
                        name="productDetails.merchantTemplateUrl"
                        label="Template https://www.omne.com?user={{omneid}}&code={{vcode}}"
                      />
                      {isAuctionSpecificPeriod ? (
                        <>
                          <DateTimeField
                            name="productDetails.startDate"
                            label="Start Date (UTC)"
                          />
                          <DateTimeField
                            name="productDetails.expirationDate"
                            label="End Date (UTC)"
                            dateNotBefore="productDetails.startDate"
                          />
                        </>
                      ) : (
                        <>
                          <DateField
                            name="productDetails.startDate"
                            label="Start Date"
                            disablePast
                          />
                          <DateField
                            name="productDetails.expirationDate"
                            label="End Date"
                            dateNotBefore="productDetails.startDate"
                            disablePast
                          />
                        </>
                      )}
                      <SelectField
                        name="productDetails.paymentType"
                        label="Payment Type"
                        options={[
                          { value: PAYMENT_TYPE.FIAT, label: "FIAT" },
                          { value: PAYMENT_TYPE.POINTS, label: "POINTS" },
                        ]}
                        disabled={isHaveAuction}
                      />
                      {isHaveAuction && (
                        <TextField
                          name="productDetails.insuranceBidFee"
                          label="Insurance Bid Fee"
                          isNumeric={true}
                          disabled={true}
                          isEndAdornment
                        />
                      )}
                    </Stack>
                    <Stack spacing={2} width={"-webkit-fill-available"}>
                      <SelectField
                        name="productDetails.productCategory"
                        label="Product Category"
                        options={[
                          {
                            value: PRODUCT_CATEGORY.CAMPAIGN,
                            label: "Campaign",
                          },
                          {
                            value: PRODUCT_CATEGORY.REWARD,
                            label: "Reward",
                          },
                          {
                            value: PRODUCT_CATEGORY.AUCTION,
                            label: "Auction",
                          },
                        ]}
                      />
                      <SelectField
                        name="productDetails.productType"
                        label="Product Type"
                        disabledForEdit={true}
                        options={[
                          { value: PRODUCT_TYPE.VOUCHERS, label: "Vouchers" },
                          {
                            value: PRODUCT_TYPE.DIGITAL_GOODS_AND_SERVICES,
                            label: "Digital goods and services",
                          },
                          {
                            value: PRODUCT_TYPE.PHYSICAL_MERCHANDISE,
                            label: "Physical Merchandise",
                          },
                        ]}
                      />
                      <SelectField
                        name="productDetails.voucherType"
                        label="Voucher Type"
                        options={[
                          { value: VOUCHER_TYPE.Code, label: "Alphanumeric" },
                          { value: VOUCHER_TYPE.QRCode, label: "QRCode" },
                        ]}
                        dependsOn={{
                          field: "productDetails.productType",
                          value: PRODUCT_TYPE.VOUCHERS,
                          required: true,
                        }}
                      />
                      <SelectField
                        name="productDetails.isOnDemand"
                        label="Voucher on demand"
                        options={[
                          { value: IS_ON_DEMAND.On, label: "On" },
                          { value: IS_ON_DEMAND.Off, label: "Off" },
                        ]}
                        transform={(value: string) =>
                          value === IS_ON_DEMAND.On ? true : false
                        }
                        parse={(value: boolean) =>
                          value ? IS_ON_DEMAND.On : IS_ON_DEMAND.Off
                        }
                        disabled={isHaveAuction}
                      />
                      <TextField
                        name="productDetails.originalPrice"
                        label="Original Price (Optional)"
                        isNumeric={true}
                      />
                      <SelectField
                        name="productDetails.auctionType"
                        disabled={!isHaveAuction}
                        label="Auction Type"
                        options={[
                          {
                            value: AUCTION_TYPE.HOURS24,
                            label: "24 Hours",
                          },
                          {
                            value: AUCTION_TYPE.SPECIFIC,
                            label: "Specific Period",
                          },
                        ]}
                      />
                      <TextField
                        name="productDetails.promotionalPrice"
                        label={
                          isHaveAuction
                            ? "Initial bid points"
                            : "Promotional Price"
                        }
                        isNumeric={true}
                        compare={{
                          lessThan: "productDetails.originalPrice",
                          message:
                            "Promotional price cannot be greater than original price",
                        }}
                      />
                      <TextField
                        name="productDetails.denomination"
                        label="Face value"
                        isNumeric={true}
                      />
                      {isHaveAuction && (
                        <TextField
                          name="productDetails.noninsuranceBidFee"
                          label="Non Insurance Bid Fee"
                          isNumeric={true}
                          disabled={true}
                          isEndAdornment
                        />
                      )}
                    </Stack>
                  </>
                </FormSection>
                <FormSection
                  sectionTitle="Cohort User Management"
                  direction="row"
                >
                  <>
                    <Stack spacing={2} width={"-webkit-fill-available"}>
                      <SelectField
                        name="productDetails.cohortType"
                        disabled={isCampaign}
                        label="Cohort"
                        options={[
                          {
                            value: COHORT_TYPE.SPECIFICUSER,
                            label: "Specific Users (GUID)",
                          },
                          {
                            value: COHORT_TYPE.INSURANCE,
                            label: COHORT_TYPE.INSURANCE,
                          },
                          {
                            value: COHORT_TYPE.INSURANCESCB,
                            label: COHORT_TYPE.INSURANCESCB,
                          },
                          {
                            value: COHORT_TYPE.INSURANCEFWD,
                            label: COHORT_TYPE.INSURANCEFWD,
                          },
                          {
                            value: COHORT_TYPE.INACTIVEINSURANCE,
                            label: COHORT_TYPE.INACTIVEINSURANCE,
                          },
                          {
                            value: COHORT_TYPE.NON_INSURANCE,
                            label: "Non-Insurance",
                          },
                          {
                            value:
                              COHORT_TYPE.NON_INSURANCEANDINACTIVEINSURANCE,
                            label: "Non-Insurance and InactiveInsurance",
                          },
                          {
                            value: COHORT_TYPE.ALL,
                            label: COHORT_TYPE.ALL,
                          },
                        ]}
                      />
                      {isSpecificUser && (
                        <>
                          <Tooltip title="Please note that this action will overwrite the existing list of specific user.">
                            <Button
                              data-testid="create_product-specific_user-upload_csv-button"
                              fullWidth
                              variant={isDisabled ? "disabled" : "secondary"}
                              endIcon={
                                isUploading ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <UploadIcon
                                    height={18}
                                    stroke={
                                      isUploadDisabled
                                        ? mono.grey[3]
                                        : mono.black
                                    }
                                  />
                                )
                              }
                              disabled={isUploadDisabled || isUploading}
                              onClick={onClickUpload}
                            >
                              <Text variant="title_small" py={1}>
                                Upload(CSVS)
                              </Text>
                            </Button>
                          </Tooltip>
                          <input
                            ref={inputRef}
                            hidden
                            type="file"
                            aria-label="select files"
                            multiple={false}
                            accept={".csv"}
                            onClick={(
                              event: React.MouseEvent<
                                HTMLInputElement,
                                MouseEvent
                              >,
                            ) => {
                              const element = event.target as HTMLInputElement;
                              element.value = "";
                            }}
                            onChange={onHandleFileChange}
                          />
                        </>
                      )}
                      <TextField
                        name="productDetails.pointsFrom"
                        label="Available Points From"
                        isNumeric={true}
                        disabled={!isReward}
                      />
                      <SelectField
                        name="productDetails.userLimitFrequency"
                        label="User limit frequency"
                        options={[
                          {
                            value: LIMIT_FREQUENCY.DAILY,
                            label: LIMIT_FREQUENCY.DAILY,
                          },
                          {
                            value: LIMIT_FREQUENCY.MONTHLY,
                            label: LIMIT_FREQUENCY.MONTHLY,
                          },
                          {
                            value: LIMIT_FREQUENCY.FOREVER,
                            label: LIMIT_FREQUENCY.FOREVER,
                          },
                          { label: "Empty", value: "" },
                        ]}
                        disabled={isHaveAuction || isCampaign}
                      />
                    </Stack>
                    <Stack
                      spacing={2}
                      width={"-webkit-fill-available"}
                      justifyContent="flex-end"
                    >
                      {isSpecificUser && (
                        <>
                          <Button
                            data-testid="create_product-product_inventory-download_csv-button"
                            fullWidth
                            variant={isDisabled ? "disabled" : "secondary"}
                            endIcon={
                              isDownloading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon
                                  height={18}
                                  stroke={
                                    isDisabled ? mono.grey[3] : mono.black
                                  }
                                />
                              )
                            }
                            disabled={isDisabled || isDownloading}
                            onClick={onClickDownload}
                          >
                            <Text variant="title_small" py={1}>
                              Download(CSVS)
                            </Text>
                          </Button>
                        </>
                      )}
                      <TextField
                        name="productDetails.pointsTo"
                        label="Available Points To"
                        isNumeric={true}
                        disabled={!isReward}
                      />
                      <TextField
                        name="productDetails.userLimitNumber"
                        label="User limit number"
                        isNumeric={true}
                        disabled={
                          isNoneUserLimitFrequency ||
                          isHaveAuction ||
                          isCampaign
                        }
                        limitNumber={12}
                      />
                    </Stack>
                  </>
                </FormSection>
                <FormSection
                  sectionTitle="Voucher Limit Management"
                  direction="row"
                >
                  <>
                    <Stack
                      spacing={2}
                      width={"-webkit-fill-available"}
                      justifyContent="flex-end"
                    >
                      <SelectField
                        name="productDetails.voucherLimitFrequency"
                        label="Voucher Limit frequency"
                        options={[
                          {
                            value: LIMIT_FREQUENCY.DAILY,
                            label: LIMIT_FREQUENCY.DAILY,
                          },
                          {
                            value: LIMIT_FREQUENCY.MONTHLY,
                            label: LIMIT_FREQUENCY.MONTHLY,
                          },
                          { label: "Empty", value: "" },
                        ]}
                        disabled={isHaveAuction}
                      />
                    </Stack>
                    <Stack
                      spacing={2}
                      width={"-webkit-fill-available"}
                      justifyContent="flex-end"
                    >
                      <TextField
                        name="productDetails.voucherLimitNumber"
                        label="Voucher limit number"
                        isNumeric={true}
                        disabled={isNoneVoucherLimitFrequency || isHaveAuction}
                        limitNumber={12}
                      />
                    </Stack>
                  </>
                </FormSection>
                <FormSection sectionTitle="Country">
                  <ToggleField
                    name="productDetails.productCountry"
                    label="Country"
                    options={countriesOption}
                    exclusive
                    disabledForEdit
                  />
                </FormSection>
                <LanguageFormSection
                  sectionTitle="Language"
                  options={[
                    { value: "translationDetails.en-GB", label: "English" },
                    { value: "translationDetails.ja-JP", label: "Japanese" },
                    { value: "translationDetails.vi-VN", label: "Vietnamese" },
                    { value: "translationDetails.km-KH", label: "Khmer" },
                    { value: "translationDetails.id-ID", label: "Indonesian" },
                    { value: "translationDetails.th-TH", label: "Thai" },
                    {
                      value: "translationDetails.zh-HK",
                      label: "Chinese (Traditional)",
                    },
                    {
                      value: "translationDetails.zh-CN",
                      label: "Chinese (Simplified)",
                    },
                  ]}
                  requiredValue="translationDetails.en-GB"
                />
              </Stack>
              <Stack direction="column" spacing={2} width={379}>
                <FormSection
                  sectionTitle="Upload images"
                  sectionDescription="Please upload PNG or JPG image format *Maximum upload size: 10 MB"
                >
                  <>
                    <ImageUploadField
                      name="productDetails.productBanner"
                      label="Banner image"
                      imageSize={IMAGE_SIZE.WIDE}
                    />
                    <SelectField
                      name="productDetails.promoTag"
                      label="Promo Tag"
                      options={[
                        { value: PROMO_TAG.HOT, label: "HOT" },
                        {
                          value: PROMO_TAG.JUST_FOR_YOU,
                          label: "JUST FOR YOU",
                        },
                        { value: PROMO_TAG.EXCLUSIVE, label: "EXCLUSIVE" },
                      ]}
                    />

                    <ImageUploadField
                      name="productDetails.brandLogo"
                      label="Brand logo"
                      imageSize={IMAGE_SIZE.PANORAMIC}
                    />
                    <ImageUploadField
                      name="productDetails.productImage"
                      label="Thumbnail"
                      imageSize={IMAGE_SIZE.SQUARE}
                    />
                    <ImageUploadField
                      name="productDetails.redemptionUrlImage"
                      label="Redemption Image"
                      imageSize={IMAGE_SIZE.WIDE}
                    />
                  </>
                </FormSection>
                <ProductInventory productStatus={productDetails?.status} />
              </Stack>
            </Stack>
          </form>
        </Stack>
      </FormProvider>
      <DevTool control={formMethods.control} />
    </Box>
  );
};

export default ProductForm;
